import React from 'react';
import { getIvTooltip } from '../utils/dataSourceTooltip';

function GreeksMarketSection({ contract, simulationData, loading, error }) {

  const formatGreek = (value) => {
    if (value == null || value === 'NA' || isNaN(value)) return 'NA';
    return Number(value).toFixed(4);
  };

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatNumber = (value) => {
    if (value == null || isNaN(value)) return '--';
    return Number(value).toLocaleString();
  };

  const formatPercentage = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `${Number(value).toFixed(2)}%`;
  };

  const formatPercentChange = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value);
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    const symbol = num >= 0 ? '↑' : '↓';
    return <span className={color}>{symbol} {Math.abs(num).toFixed(1)}%</span>;
  };

  const renderGreekRow = (label, initial, current, pctChange, formatter, tooltip) => {
    const hasInitial = initial != null && !isNaN(initial);
    return (
      <div className="flex justify-between items-start">
        <span className="text-gray-400 text-[10px]">{label}:</span>
        <div className={`text-right ${tooltip ? 'iv-source-hint' : ''}`} title={tooltip || undefined}>
          {hasInitial ? (
            <>
              <div className="text-white text-[10px] leading-tight">
                {formatter(initial)} → {formatter(current)}
              </div>
              <div className="text-[9px] leading-tight">
                {formatPercentChange(pctChange)}
              </div>
            </>
          ) : (
            <div className="text-white text-[10px]">{formatter(current)}</div>
          )}
        </div>
      </div>
    );
  };

  // Map persisted-field suffix <-> live simulationData key.
  const simKey = { delta: 'Delta', gamma: 'Gamma', theta: 'Theta', vega: 'Vega', rho: 'Rho', iv: 'Implied Volatility' };

  // For each Greek, resolve a value via this priority chain:
  //   1. persisted current_* from the contract (set at save / on each refresh)
  //   2. live simulationData value (lets legacy contracts that predate the tracking migration show
  //      a value before they've been manually refreshed)
  // If neither is available the row falls through to its NA branch. The initial value follows the
  // same chain but falls back to whatever current resolved to — so legacy cards render
  // `{today} → {today}` and `↑ 0.0%` instead of a sea of NA.
  const resolveGreek = (key) => {
    const persistedCurrent = contract[`current_${key}`];
    const persistedInitial = contract[`initial_${key}`];
    const live = simulationData ? simulationData[simKey[key]] : null;
    const cleanLive = (live === 'NA' || live == null || isNaN(Number(live))) ? null : Number(live);

    const current = persistedCurrent != null ? persistedCurrent : cleanLive;
    const initial = persistedInitial != null ? persistedInitial : current;
    const pct = persistedInitial != null ? contract[`${key}_percent_change`] : 0;
    return { initial, current, pct };
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          <span className="text-sm text-gray-400">Loading market data...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-2">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      );
    }

    return (
      <div className="bg-gray-800/50 rounded p-2 text-xs space-y-1.5">
        {/* Greeks Section — reads from persisted contract fields, renders without waiting on live simulation */}
        <div>
          <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
            🎯 Option Greeks
          </div>
          {(() => {
            const d = resolveGreek('delta');
            const g = resolveGreek('gamma');
            const t = resolveGreek('theta');
            const v = resolveGreek('vega');
            const r = resolveGreek('rho');
            const iv = resolveGreek('iv');
            // The row may show the saved IV; the tooltip describes the latest simulation.
            const ivTip = getIvTooltip(simulationData, contract?.current_iv != null ? 'Latest simulation' : undefined);
            return (
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                {renderGreekRow('Delta', d.initial, d.current, d.pct, formatGreek)}
                {renderGreekRow('Theta', t.initial, t.current, t.pct, formatGreek)}
                {renderGreekRow('Gamma', g.initial, g.current, g.pct, formatGreek)}
                {renderGreekRow('Vega', v.initial, v.current, v.pct, formatGreek)}
                {renderGreekRow('Rho', r.initial, r.current, r.pct, formatGreek)}
                {renderGreekRow('IV', iv.initial, iv.current, iv.pct, formatPercentage, ivTip)}
              </div>
            );
          })()}
        </div>

        {/* Market Data Section — gated on live simulationData (Volume/OI not persisted on contract) */}
        <div className="pt-1.5 border-t border-gray-700">
          <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
            📊 Market Data
          </div>
          {simulationData ? (
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Volume:</span>
                <span className="text-white text-[10px]">{formatNumber(simulationData.Volume)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Open Int:</span>
                <span className="text-white text-[10px]">{formatNumber(simulationData['Open Interest'])}</span>
              </div>
            </div>
          ) : (
            <div className="text-[10px] text-gray-500">No live market data</div>
          )}
        </div>

        {/* Sell Metrics - Only shown for sell positions */}
        {contract.position_type === 'sell' && simulationData && (
          <div className="pt-1.5 border-t border-gray-700">
            <div className="font-semibold text-gray-300 mb-1 border-b border-gray-600 pb-0.5 text-[11px]">
              💰 Sell Metrics
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Collateral:</span>
                <span className="text-white text-[10px]">{formatCurrency(simulationData['Collateral Required'])}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Return on Collat:</span>
                <span className="text-white text-[10px]">{formatPercentage(simulationData['Return on Collateral %'])}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Max Profit:</span>
                <span className="text-green-400 text-[10px]">{formatCurrency(simulationData['Max Profit'])}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-[10px]">Max Loss:</span>
                <span className={`text-[10px] ${simulationData['Max Loss'] === 'Unlimited' ? 'text-orange-400' : 'text-red-400'}`}>
                  {simulationData['Max Loss'] === 'Unlimited' ? 'Unlimited' : formatCurrency(simulationData['Max Loss'])}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-300 mb-2">🎯 Greeks & Market Data</h4>
      {renderContent()}
    </div>
  );
}

export default GreeksMarketSection;